import { Injectable } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { UserManagementService } from '../../shared/services/user-management.service';
import {
  DeleteScheduleMutation,
  DeleteScheduleMutationVariables,
  DeleteScheduleResponse,
  GetQuerySchedulersQuery,
  GetQuerySchedulersQueryVariables,
  QuerySchedulers,
  ScheduleType,
  SchedulerStatus,
  UpdateScheduleStatusInput,
  UpdateScheduleStatusMutation,
  UpdateScheduleStatusMutationVariables,
} from '../../../API';
import { GetQuerySchedulers } from '../../../graphql/queries';
import { Status, Type } from '../models/scheduling-model';
import { DeleteSchedule, UpdateScheduleStatus } from '../../../graphql/mutations';
import dayjs from 'dayjs';

export interface SchedulerRecord {
  schedulerId: string;
  name: string;
  queryId: string;
  queryName: string;
  type: Type;
  createdBy: string;
  schedulerStatus: Status;
  createdAt: string;
  startAt: string;
  lastModifiedAt: string;
  timezone: string;
  schedulerDescription: string;
  nextRunOn: string;
  nextRunOnSuffix: string;
  scheduleEndsOn: string;
  lastModifiedBy: string;
}

@Injectable({
  providedIn: 'root',
})
export class SchedulingService {
  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
  ) {}

  async getSchedulerList(): Promise<SchedulerRecord[]> {
    const records = await this.getSchedulerRecordsByOrgId();
    return records.map((record) => this.mapSchedulerRows(record));
  }

  private async getSchedulerRecordsByOrgId(): Promise<QuerySchedulers[]> {
    const orgId = this.userManagementService.getOrgId();
    if (!orgId) {
      throw new Error('Unable to load scheduler list: orgId is missing for the current user.');
    }
    const records = await this.apiService.query<GetQuerySchedulersQuery, GetQuerySchedulersQueryVariables>(
      GetQuerySchedulers,
      {
        orgId,
      },
    );

    return records?.data?.GetQuerySchedulers ?? [];
  }

  async deleteSchedule(schedulerId: string, deletionType: string): Promise<DeleteScheduleResponse | null> {
    const orgId = this.userManagementService.getOrgId();
    if (!orgId) {
      throw new Error('Unable to delete scheduler: orgId is missing for the current user.');
    }
    const response = await this.apiService.query<DeleteScheduleMutation, DeleteScheduleMutationVariables>(
      DeleteSchedule,
      {
        input: {
          orgId: orgId,
          schedulerId: schedulerId,
          deletionType: deletionType,
        },
      },
    );
    return response.data?.DeleteSchedule ?? null;
  }

  async updateSchedule(schedulerId: string, status: SchedulerStatus): Promise<QuerySchedulers | null> {
    const orgId = this.userManagementService.getOrgId();
    const userEmail = this.userManagementService.getEmail();
    if (!orgId || !userEmail) {
      throw new Error('Unable to delete scheduler: missing user info.');
    }
    const payload: UpdateScheduleStatusInput = {
      orgId: orgId,
      lastModifiedBy: userEmail,
      schedulerId: schedulerId,
      status: status,
    };
    const response = await this.apiService.query<UpdateScheduleStatusMutation, UpdateScheduleStatusMutationVariables>(
      UpdateScheduleStatus,
      { input: payload },
    );
    return response?.data?.UpdateScheduleStatus ?? null;
  }

  private mapSchedulerRows(record: QuerySchedulers): SchedulerRecord {
    const nextRun = this.getNextRunDisplay(record);
    const scheduleEndsOn = this.getSchedulerEndsOn(record);
    return {
      schedulerId: record.schedulerId,
      name: record?.name.trim() ?? '',
      queryId: record?.queryId,
      queryName: record?.queryName?.trim() ?? '',
      type: this.mapSchedulerType(record?.type),
      createdBy: record?.createdBy ?? '',
      schedulerStatus: this.mapSchedulerStatus(record?.schedulerStatus, scheduleEndsOn),
      createdAt: record?.createdAt ?? '',
      startAt: record?.startAt ?? '',
      lastModifiedAt: record?.lastModifiedAt ?? '',
      timezone: record?.timezone ?? '',
      schedulerDescription: record.schedulerDescription ?? '',
      nextRunOn: nextRun,
      nextRunOnSuffix: this.getNextRunSuffix(nextRun),
      scheduleEndsOn: scheduleEndsOn,
      lastModifiedBy: record?.lastModifiedBy?.trim() ?? '',
    };
  }

  private mapSchedulerStatus(status: SchedulerStatus | null | undefined, scheduleEndDate: string): Status {
    if (scheduleEndDate !== '-' || scheduleEndDate !== null || scheduleEndDate !== undefined) {
      const endDateTime = dayjs(scheduleEndDate);
      const currentDateTime = dayjs();
      const isExpired = currentDateTime.isAfter(endDateTime);

      if (isExpired) {
        return 'Expired';
      }
    }
    switch (status) {
      case SchedulerStatus.ACTIVE:
        return 'Active';
      case SchedulerStatus.PAUSED:
        return 'Paused';
      default:
        return 'Expired';
    }
  }

  private mapSchedulerType(type: ScheduleType | null | undefined) {
    switch (type) {
      case ScheduleType.ONE_TIME:
        return 'One-Time';
      default:
        return 'Recurring';
    }
  }

  private getNextRunDisplay(scheduler: QuerySchedulers): string {
    const nextRun = this.getNextRun(scheduler);
    if (!nextRun) {
      return '';
    }

    return this.formatDate(nextRun);
  }

  private getSchedulerEndsOn(scheduler: QuerySchedulers): string {
    const end = scheduler.end;
    const startAt = scheduler.startAt;
    if (scheduler?.type === ScheduleType.ONE_TIME && startAt !== null && startAt !== undefined) {
      return this.formatDate(new Date(startAt));
    }

    if (!end || end.type === 'NEVER') {
      return '-';
    }

    // ON_DATE
    if (end.type === 'ON_DATE' && end.endAt !== null && end.endAt !== undefined) {
      const endDate = new Date(end.endAt);
      return this.formatDate(endDate);
    }

    // AFTER_OCCURRENCES
    if (end.type === 'AFTER_OCCURRENCES') {
      const endDate = this.calculateEndDateForOccurrences(scheduler);
      if (endDate !== null && endDate !== undefined) {
        return this.formatDate(endDate);
      }
    }

    return '—';
  }

  private formatDate(date: Date): string {
    return date == null || undefined
      ? ''
      : new Date(date).toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
  }

  private calculateEndDateForOccurrences(scheduler: QuerySchedulers): Date | null {
    const max = scheduler.end?.maxOccurrences;
    if (!max) return null;

    const occurrences = this.getOccurrences(scheduler, max);
    return occurrences;
  }

  private getOccurrences(scheduler: any, count: number): Date {
    let current = new Date(scheduler.startAt);

    for (let i = 1; i < count; i++) {
      const nextDate = this.getNextRun(scheduler, current);
      if (nextDate !== null) {
        current = nextDate;
        current = new Date(current.getTime() + 1);
      }
    }
    return current;
  }

  private getNextRun(scheduler: QuerySchedulers, now = new Date()): Date | null {
    const WEEKDAY_MAP: Record<string, number> = {
      SUNDAY: 0,
      MONDAY: 1,
      TUESDAY: 2,
      WEDNESDAY: 3,
      THURSDAY: 4,
      FRIDAY: 5,
      SATURDAY: 6,
    };

    const start = this.toDate(scheduler.startAt);
    if (!start) return null;

    // END constraint
    const endDate = this.toDate(scheduler.end?.endAt);

    // ONE TIME
    if (scheduler.type === 'ONE_TIME') {
      return start > now ? start : null;
    }

    const recurrence = scheduler.recurrence;
    if (!recurrence) return null;

    // DAILY
    if (recurrence.frequency === 'DAILY') {
      const interval = recurrence.interval ?? 1;
      const next = new Date(start);

      while (next <= now) {
        next.setDate(next.getDate() + interval);
      }

      if (endDate && next > endDate) return null;
      return next;
    }

    // WEEKLY
    if (recurrence.frequency === 'WEEKLY') {
      const interval = recurrence.interval ?? 1;
      const days = recurrence.weekly?.days ?? [];

      if (!days.length) return null;

      const dayNumbers = days.map((d: string) => WEEKDAY_MAP[d]).sort();

      const candidate = new Date(start);

      while (true) {
        const diffWeeks = Math.floor((candidate.getTime() - start.getTime()) / (7 * 86400000));

        if (diffWeeks % interval === 0) {
          for (const day of dayNumbers) {
            const run = new Date(candidate);
            run.setDate(run.getDate() - run.getDay() + day);

            if (run > now && run >= start) {
              if (!endDate || run <= endDate) return run;
            }
          }
        }
        candidate.setDate(candidate.getDate() + 7);
        if (endDate && candidate > endDate) return null;
      }
    }

    // MONTHLY (DAY_OF_MONTH)
    if (recurrence.frequency === 'MONTHLY') {
      const interval = recurrence.interval ?? 1;
      const next = new Date(start);

      while (next <= now) {
        next.setMonth(next.getMonth() + interval);
      }

      if (endDate && next > endDate) return null;
      return next;
    }

    return null;
  }

  private toDate(value?: string | null): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  getNextRunSuffix(nextRun: string): string {
    if (nextRun === '') {
      return '';
    }
    const days = this.daysFromNow(new Date(nextRun));
    const dayLabel = days === 0 ? 'today' : `in ${days} days`;

    return `, ${dayLabel}`;
  }

  daysFromNow(date: Date): number {
    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const startOfTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffMs = startOfTarget.getTime() - startOfToday.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }
}
